import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Clock, MapPin, Phone } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { FORM_ENDPOINT, FORM_SUCCESS_REDIRECTS, isSpamSubmission } from "@/lib/forms";

const AppointmentForm = () => {
  const [date, setDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const { toast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Nettoyage du timeout au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const availableTimes = [
    "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
    "11:00", "11:30", "14:00", "14:30", "15:00", "15:30",
    "16:00", "16:30", "17:00", "17:30"
  ];

  const services = [
    "Devis gratuit",
    "Réparation de toiture",
    "Rénovation complète",
    "Nettoyage / Démoussage",
    "Isolation de combles",
    "Gouttières",
    "Urgence / Fuite",
    "Autre"
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation anti-spam honeypot
    if (isSpamSubmission(honeypot)) {
      toast({
        title: "Erreur",
        description: "Soumission invalide détectée.",
        variant: "destructive"
      });
      return;
    }

    // Validation des champs obligatoires
    if (!formData.name.trim() || !formData.phone.trim() || !date || !selectedTime) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    // Validation des longueurs
    if (formData.name.trim().length > 100 || formData.phone.trim().length > 20 || 
        formData.email.trim().length > 255 || formData.address.trim().length > 200 ||
        formData.message.trim().length > 1000) {
      toast({
        title: "Erreur",
        description: "Certains champs dépassent la longueur maximale autorisée",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Timeout de sécurité
    timeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: "Problème de connexion",
        description: "La requête prend trop de temps. Veuillez réessayer.",
        variant: "destructive",
      });
    }, 12000);

    try {
      const submitData = new FormData();
      submitData.append('_subject', `Demande de rendez-vous - ${formData.name}`);
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('address', formData.address);
      submitData.append('service', formData.service);
      submitData.append('date_rendez_vous', date ? format(date, 'EEEE dd MMMM yyyy', { locale: fr }) : '');
      submitData.append('heure_rendez_vous', selectedTime);
      submitData.append('message', formData.message);
      submitData.append('website', honeypot);

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        body: submitData,
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (response.ok) {
        toast({
          title: "Demande envoyée",
          description: "Nous vous contacterons rapidement pour confirmer votre rendez-vous.",
        });
        setFormData({
          name: "",
          phone: "",
          email: "",
          address: "",
          service: "",
          message: ""
        });
        setDate(undefined);
        setSelectedTime("");
      } else {
        throw new Error('Erreur serveur');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Erreur d'envoi",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    console.log("[WhatsApp] click");
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast({
        title: "Informations manquantes",
        description: "Renseignez au minimum votre nom et téléphone pour WhatsApp.",
        variant: "destructive",
      });
      return;
    }

    const dateText = date ? format(date, 'EEEE dd MMMM yyyy', { locale: fr }) : 'Non spécifiée';
    const timeText = selectedTime || 'Non spécifiée';

    const message = `🏠 *Demande de RDV - Jacky Couverture*

👤 *Client :* ${formData.name}
📞 *Téléphone :* ${formData.phone}
${formData.email ? `📧 *Email :* ${formData.email}` : ''}
📅 *Date souhaitée :* ${dateText}
⏰ *Heure :* ${timeText}
${formData.address ? `📍 *Adresse :* ${formData.address}` : ''}
🔧 *Service :* ${formData.service || 'Non spécifié'}
${formData.message ? `💬 *Message :* ${formData.message}` : ''}`;

    const whatsappUrl = `https://wa.me/33646702617?text=${encodeURIComponent(message)}`;

    try {
      const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.warn('[WhatsApp] popup blocked, redirecting in same tab');
        window.location.href = whatsappUrl;
        return;
      }
      newWindow.opener = null;
    } catch {
      // Fallback in case window.open throws due to browser settings
      window.location.href = whatsappUrl;
    }
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = date.getDay();
    return date < today || dayOfWeek === 0; // Disable past dates and Sundays
  };

  const dateTextDisplay = date ? format(date, 'EEEE dd MMMM yyyy', { locale: fr }) : 'Non spécifiée';
  const timeTextDisplay = selectedTime || 'Non spécifiée';
  const whatsappMessage = `🏠 *Demande de RDV - Jacky Couverture*

👤 *Client :* ${formData.name}
📞 *Téléphone :* ${formData.phone}
${formData.email ? `📧 *Email :* ${formData.email}` : ''}
📅 *Date souhaitée :* ${dateTextDisplay}
⏰ *Heure :* ${timeTextDisplay}
${formData.address ? `📍 *Adresse :* ${formData.address}` : ''}
🔧 *Service :* ${formData.service || 'Non spécifié'}
${formData.message ? `💬 *Message :* ${formData.message}` : ''}`;

  const whatsappUrl = `https://wa.me/33646702617?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <section className="py-24 bg-gradient-to-b from-background-gray to-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 bg-accent/10 text-accent font-semibold rounded-full mb-6 text-sm uppercase tracking-wider">
              Rendez-vous
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-primary">
              Planifier un Rendez-vous
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Choisissez votre créneau pour un devis gratuit à domicile
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span>Intervention sous 48h</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span>Déplacement gratuit 30km</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary" />
                <span>Devis par WhatsApp</span>
              </div>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="bg-card rounded-2xl p-10 shadow-2xl border border-border/50"
          >
            {/* Honeypot anti-spam (invisible) */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="grid md:grid-cols-2 gap-10">
              {/* Left Column - Contact Info */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold mb-6 text-primary">Vos Informations</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Nom et Prénom *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="06 12 34 56 78"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="jean.dupont@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse des travaux</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    placeholder="123 rue de la Paix, Treillières"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service">Type de service</Label>
                  <Select name="service" value={formData.service} onValueChange={(value) => handleInputChange("service", value)}>
                    <SelectTrigger aria-label="Sélectionnez un type de service">
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="service" value={formData.service} />
                </div>
              </div>

              {/* Right Column - Date & Time */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold mb-6 text-primary">Planification</h3>
                
                <div className="space-y-2">
                  <Label>Date souhaitée *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "EEEE dd MMMM yyyy", { locale: fr }) : "Sélectionner une date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={isDateDisabled}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Créneau horaire *</Label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger aria-label="Choisir un créneau horaire">
                      <SelectValue placeholder="Choisir un horaire" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimes.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message complémentaire</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange("message", e.target.value)}
                    placeholder="Décrivez brièvement vos besoins..."
                    rows={4}
                  />
                </div>

                <div className="pt-6 space-y-3">
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg py-7 font-bold" 
                    size="lg"
                  >
                    {isSubmitting ? "Envoi en cours..." : "Envoyer la demande par email"}
                  </Button>
                  <Button 
                    variant="outline" 
                    asChild
                    className="w-full text-lg py-7 px-8 font-semibold rounded-md"
                  >
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="Envoyer via WhatsApp">
                      Ou envoyer via WhatsApp
                    </a>
                  </Button>
                  <div className="mt-2 text-xs text-muted-foreground/50 text-center">Form v-email</div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>* Champs obligatoires</p>
              <p className="mt-2">
                En soumettant ce formulaire, vous acceptez d'être contacté par email ou WhatsApp pour planifier votre rendez-vous.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AppointmentForm;